import type { Tile, Turn } from '../game/types.js';
import type { DiceRollProof } from '../network/protocol.js';
import type { VariantId } from '../game/variants/index.js';
import type { Difficulty } from '../settings/types.js';

// v1: initial domino schema. Replay reproduces a game by re-creating the
// initial state from `seed`/`variant`/`playerCount`/`pointGoal`/`mugginsStrict`
// in the seed and re-applying each `RecordedMove` in order. Drawn tiles are
// not stored — they're deterministic from the seed and prior draws.
//
// v2: optional `drandChainHash` + per-hand `proof` on `RecordedSeed` and on
// `NextHand` so the verifier modal (Phase C) can re-derive the shuffle seed
// from the drand round and confirm it matches the recorded deal. Old records
// without these fields render as non-verifiable rows; no migration needed.
//
// v3: the spinner became the first double *covered* rather than the first
// played (`game/Chain.ts`). The record shape is unchanged, but replay re-applies
// moves through the live engine, and a v2 game where one double was laid first
// while a different one was covered first now resolves its branch to the other
// tile — so the recorded arm indices no longer describe the same layout.
//
// Bumping this version (or any breaking shape change) invalidates older slots.
export const HISTORY_SCHEMA_VERSION = 3;

export type { DiceRollProof };

/**
 * Who controlled the local seat(s) at the time of the game. `ai` is vs-AI,
 * `local` is hot-seat (the device hands between seats), `online` is multi-
 * device through the WS server.
 */
export type RecordedMode = 'ai' | 'local' | 'online';

export interface PlayMove {
  readonly kind: 'play';
  readonly seat: number;
  readonly tile: Tile;
  /** -1 for the opening lead, otherwise the arm index. */
  readonly armIndex: number;
  /** Visual fold chosen at play time; replay uses this to reproduce chain geometry. */
  readonly turn: Turn;
  /** Points scored by this play (0 if the open-ends sum is not a multiple of N). */
  readonly points: number;
}

export interface DrawMove {
  readonly kind: 'draw';
  readonly seat: number;
}

export interface PassMove {
  readonly kind: 'pass';
  readonly seat: number;
}

export interface MugginsClaim {
  readonly kind: 'claim-muggins';
  readonly seat: number;
  readonly points: number;
}

export interface MugginsExpire {
  readonly kind: 'expire-muggins';
}

export interface NextHand {
  readonly kind: 'next-hand';
  readonly seed: number;
  /** drand proof for this hand's shuffle when the match runs in verifiable mode. */
  readonly proof?: DiceRollProof;
}

export type RecordedMove =
  | PlayMove
  | DrawMove
  | PassMove
  | MugginsClaim
  | MugginsExpire
  | NextHand;

export interface RecordedSeed {
  readonly mode: RecordedMode;
  readonly variant: VariantId;
  readonly playerCount: 2 | 3 | 4;
  /**
   * The rule set's actual match target — a Settings choice for fives variants,
   * the fixed 101 for Козёл (hence `number`, not the Settings `PointGoal`).
   */
  readonly pointGoal: number;
  readonly mugginsStrict: boolean;
  /** Козёл 13-point opening. Absent on records made before Козёл — read as false. */
  readonly opening13?: boolean;
  /** Козёл «Лысый» finish. Absent on records made before Козёл — read as false. */
  readonly baldFinish?: boolean;
  /** Initial-hand seed; subsequent hands carry their own `next-hand` seed in `moves`. */
  readonly seed: number;
  readonly difficulty: Difficulty | null;
  readonly playerName: string;
  /**
   * The seat the local viewer was on. `null` for hot-seat games where every
   * seat is local. Replay uses this to default the south-zone occupant.
   */
  readonly localSeat: number | null;
  readonly roomCode: string | null;
  readonly createdAt: number;
  /**
   * drand chain hash pinned at match start when the match is running in
   * verifiable mode. The verifier modal (Phase C) uses this to resolve the
   * right beacon archive years later, even if the LoE default chain rotates.
   * Absent on non-verifiable matches and legacy v1 records.
   */
  readonly drandChainHash?: string;
  /**
   * drand proof for the first hand's shuffle. Subsequent hands carry their
   * own proof on the matching `NextHand` move. Absent on non-verifiable
   * matches and legacy v1 records.
   */
  readonly proof?: DiceRollProof;
}

export interface ActiveSession {
  readonly id: string;
  readonly version: number;
  readonly seed: RecordedSeed;
  readonly moves: readonly RecordedMove[];
  readonly updatedAt: number;
}

export interface GameRecord {
  readonly id: string;
  readonly version: number;
  readonly createdAt: number;
  readonly finishedAt: number;
  readonly mode: RecordedMode;
  readonly variant: VariantId;
  readonly playerCount: 2 | 3 | 4;
  /** Final per-seat scores at the end of the recorded match. */
  readonly finalScores: readonly number[];
  /** Winning seats (one entry for FFA, two for 2v2 partners). Empty for blocked/abandoned. */
  readonly winnerSeats: readonly number[];
  readonly result: 'win' | 'draw' | 'abandoned';
  readonly seed: RecordedSeed;
  readonly moves: readonly RecordedMove[];
}

// Build a synthetic GameRecord from an ActiveSession — used for the online
// "View saved position" path when reconnect fails. winnerSeats/finalScores
// are empty and result is 'abandoned' because the match never reached
// game-over.
export function recordFromActiveSession(session: ActiveSession): GameRecord {
  return {
    id: session.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: session.seed.createdAt,
    finishedAt: session.updatedAt,
    mode: session.seed.mode,
    variant: session.seed.variant,
    playerCount: session.seed.playerCount,
    finalScores: [],
    winnerSeats: [],
    result: 'abandoned',
    seed: session.seed,
    moves: session.moves,
  };
}

export { generateId } from '@bn-games/history-core';
